import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { Theme } from '@mui/material'
import {
  Avatar,
  Box,
  Button,
  Grid2 as Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import EditUserDialog from 'components/EditUserDialog'
import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { LedgerAccount } from 'types/ledger-account'
import type {
  ExpandedOrganizationUser,
  Organization,
  OrganizationSetting,
  UpdateOrganizationDTO,
} from 'types/organization'
import { Permissions } from 'types/permissions'
import type { Role } from 'types/role'
import type { UserData } from 'types/user'
import type { UserProfile } from 'types/user-profile'
import { getOrganizationIdsWithPermissions } from 'types/user-profile'
import DeleteOrganizationDialog from './components/DeleteOrganizationDialog'
import EditOrganizationNameDialog from './components/EditOrganizationNameDialog'
import { generatePositionColors, getManagerName } from './utils'

interface EditOrganizationProps {
  organization: Organization
  users: ExpandedOrganizationUser[]
  theme: Theme
  roles: Role[]
  onDeleteOrganization: (organizationId: string) => Promise<void>
  onUserAction: (userData: UserData, isUpdate: boolean) => Promise<void>
  hasChildren: boolean
  isSubmitting: boolean
  error: Error | null
  ledgerAccounts: LedgerAccount[]
  organizationSettings: OrganizationSetting[]
  onUpdateOrganization: (organizationId: string, updateDTO: UpdateOrganizationDTO) => Promise<void>
  userProfile: UserProfile
  setRefreshNeeded: (value: boolean) => void
  token: string
}

const EditOrganization: FC<EditOrganizationProps> = ({
  organization,
  users,
  theme,
  roles,
  onDeleteOrganization,
  onUserAction,
  hasChildren,
  isSubmitting,
  error,
  ledgerAccounts,
  organizationSettings,
  onUpdateOrganization,
  userProfile,
  setRefreshNeeded,
  token,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false)
  const [newName, setNewName] = useState(organization.name)
  const [newParentId, setNewParentId] = useState(organization.parentId || '')

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [createMode, setCreateMode] = useState(false)
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false)

  const organizationIdsCanDeleteOrganization =
    userProfile != null ? getOrganizationIdsWithPermissions(userProfile, [Permissions.ORGANIZATION_DELETE]) : []

  const hasPermissionToDeleteOrganization = organizationIdsCanDeleteOrganization.includes(organization.id)

  useEffect(() => {
    setNewParentId(organization.parentId || '')
  }, [organization])

  const positionColors = useMemo(() => {
    const positions = [...new Set(users.map((user) => user.user.position))]
    return generatePositionColors(positions)
  }, [users])

  const handleConfirmDelete = async () => {
    await onDeleteOrganization(organization.id)
    setDeleteDialogOpen(false)
  }

  const handleConfirmEditName = async () => {
    try {
      await onUpdateOrganization(organization.id, { name: newName, parentId: newParentId })
      setEditNameDialogOpen(false)
    } catch (error) {
      console.error('Error updating organization name:', error)
    }
  }

  const isRootOrganization = organization.id === organization.rootOrganizationId

  const filteredUserProfile = useMemo(
    () => ({
      ...userProfile,
      roles: userProfile.roles.filter((role) => role.organization.id !== organization.id),
    }),
    [userProfile, organization.id]
  )

  const canDelete = users.length === 0 && !hasChildren && hasPermissionToDeleteOrganization

  const displayName = organization.visible ? organization.name : getManagerName(users) || 'Manager Name'

  return (
    <>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h3" gutterBottom>
              {displayName}
            </Typography>
          </Box>
          <span>
            {organization.visible && (
              <IconButton
                disabled={!organization.visible}
                onClick={() => {
                  setEditNameDialogOpen(true)
                  setNewName(organization.name)
                }}
                sx={{ mr: 1 }}
              >
                <EditOutlined />
              </IconButton>
            )}
            <Tooltip title={canDelete ? 'Delete this team' : 'Cannot delete: team has members or subordinate teams'}>
              <IconButton
                onClick={() => setDeleteDialogOpen(true)}
                disabled={!canDelete}
                sx={{ color: theme.palette.error.main }}
              >
                <DeleteOutlined />
              </IconButton>
            </Tooltip>
          </span>
        </Box>
        <Grid container alignItems="center" justifyContent="space-between" spacing={0} mb={2}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Typography variant="body1" color="text.secondary">
              {users.length} team member
              {users.length !== 1 ? 's' : ''}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 7 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <AnimateButton>
              <Tooltip title="Add a new team member">
                <Button
                  variant="contained"
                  startIcon={<PlusOutlined />}
                  color="primary"
                  onClick={() => {
                    setSelectedUserId(null)
                    setCreateMode(true)
                    setEditUserDialogOpen(true)
                  }}
                >
                  Team Member
                </Button>
              </Tooltip>
            </AnimateButton>
          </Grid>
        </Grid>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List>
            {users.map((user) => (
              <ListItem
                key={user.user.id}
                onClick={() => {
                  setSelectedUserId(user.user.id)
                  setCreateMode(false)
                  setEditUserDialogOpen(true)
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: positionColors[user.user.position] }}>
                    {`${user.user.firstName[0]}${user.user.lastName[0]}`}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${user.user.firstName} ${user.user.lastName}`}
                  secondary={`${user.user.position} | ${user.role.name}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <DeleteOrganizationDialog
          open={deleteDialogOpen}
          organizationName={organization.name}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
        />

        <EditOrganizationNameDialog
          open={editNameDialogOpen}
          organizationName={newName}
          parentId={newParentId}
          isRootOrganization={isRootOrganization}
          isSubmitting={isSubmitting}
          userProfile={filteredUserProfile}
          onClose={() => setEditNameDialogOpen(false)}
          onConfirm={handleConfirmEditName}
          onNameChange={setNewName}
          onParentChange={setNewParentId}
        />
      </Paper>

      <EditUserDialog
        open={editUserDialogOpen}
        onClose={() => {
          setRefreshNeeded(true)
          setEditUserDialogOpen(false)
        }}
        userId={selectedUserId}
        createMode={createMode}
        selectedOrganizationId={organization.id}
      />
    </>
  )
}

export default EditOrganization
